import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetEventTimeSlotOptions,
  GetEventTimeSlotResponse,
  ListEventTimeSlotsOptions,
  ListEventTimeSlotsResponse,
  getEventTimeSlot as universalGetEventTimeSlot,
  listEventTimeSlots as universalListEventTimeSlots,
} from './bookings-availability-v2-time-slot-event-time-slots.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/bookings' };

export function listEventTimeSlots(
  httpClient: HttpClient
): ListEventTimeSlotsSignature {
  return (options?: ListEventTimeSlotsOptions) =>
    universalListEventTimeSlots(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEventTimeSlotsSignature {
  /**
   * Retrieves a list of class session time slots that match the provided filters.
   *
   * ### Defaults
   *
   * List Event Time Slots uses the following defaults:
   *
   * - `providerId` set to the Bookings app ID.
   * - `timeZone` set to the business time zone.
   * - `cursorPaging.limit` set to `50`.
   * - Returns both bookable and non-bookable time slots.
   *
   * Time slots are always sorted by their `localStartDate` in ascending order.
   *
   * ### Service type limitations
   *
   * To retrieve appointment availability, call [List Availability Time Slots](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/list-availability-time-slots).
   *
   * To retrieve course availability, follow the [End-to-End Booking Flow for courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
   *
   * ### Booking policy filtering
   *
   * Use `bookingPolicyViolations` to filter slots based on booking restrictions defined in your [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction).
   *
   * *Default behavior**: When you don't specify any `bookingPolicyViolations` filters, all slots are returned regardless of their booking policy status.
   *
   * - Set `tooEarlyToBook` to `true` to retrieve slots that can't be booked yet due to minimum advance booking time restrictions.
   * Currently, you can't specify `earliestBookingDate` to further filter by when the slot becomes bookable.
   * - Set `tooLateToBook` to `true` to retrieve slots that can no longer be booked due to maximum advance booking time restrictions.
   * - Set `bookOnlineDisabled` to `true` or `false` to filter by online booking availability settings.
   */
  (options?: ListEventTimeSlotsOptions): Promise<
    NonNullablePaths<
      ListEventTimeSlotsResponse,
      `timeSlots` | `timeSlots.${number}.location.locationType`,
      5
    >
  >;
}

export function getEventTimeSlot(
  httpClient: HttpClient
): GetEventTimeSlotSignature {
  return (eventId: string, options?: GetEventTimeSlotOptions) =>
    universalGetEventTimeSlot(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEventTimeSlotSignature {
  /**
   * Retrieves detailed information about a specific class session time slot.
   *
   *
   * Call this method after selecting a suitable slot with List Event Time Slots to obtain its full capacity and booking-policy status.
   *
   * ### Service type limitations
   *
   * To retrieve appointment availability, call [Get Availability Time Slot](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/get-availability-time-slot).
   *
   * To retrieve course availability, follow the [End-to-End Booking Flow for courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows#book-a-course).
   * @param - Event ID.
   */
  (eventId: string, options?: GetEventTimeSlotOptions): Promise<
    NonNullablePaths<
      GetEventTimeSlotResponse,
      | `timeSlot.location.locationType`
      | `timeSlot.availableResources`
      | `timeSlot.nestedTimeSlots`
      | `timeSlot.nestedTimeSlots.${number}.serviceId`
      | `timeSlot.nestedTimeSlots.${number}.localStartDate`
      | `timeSlot.nestedTimeSlots.${number}.localEndDate`,
      5
    >
  >;
}

export {
  AvailableResources,
  BookingPolicyViolations,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  CustomerChoices,
  EventInfo,
  GetAvailabilityTimeSlotRequest,
  GetAvailabilityTimeSlotRequestCustomerChoices,
  GetAvailabilityTimeSlotResponse,
  GetEventTimeSlotOptions,
  GetEventTimeSlotRequest,
  GetEventTimeSlotResponse,
  GetMultiServiceAvailabilityTimeSlotRequest,
  GetMultiServiceAvailabilityTimeSlotResponse,
  ListAvailabilityTimeSlotsRequest,
  ListAvailabilityTimeSlotsResponse,
  ListEventTimeSlotsOptions,
  ListEventTimeSlotsRequest,
  ListEventTimeSlotsResponse,
  ListMultiServiceAvailabilityTimeSlotsRequest,
  ListMultiServiceAvailabilityTimeSlotsResponse,
  Location,
  LocationType,
  NestedTimeSlot,
  NonBookableReasons,
  Resource,
  ResourceType,
  Service,
  TimeSlot,
  V2CustomerChoices,
  WaitingList,
} from './bookings-availability-v2-time-slot-event-time-slots.universal.js';
